import * as Sentry from '@sentry/react';
import { getEnvironment, Environment } from '@squareup/util-environment';
import { getSession } from '@squareup/service-session';
import { hasConsent, OneTrustConsent } from '@squareup/service-one-trust';

const SHARED_REACT_MFE_SENTRY_DSN = "https://7a8e809e0842eb3041d07653476ff659@o160250.ingest.us.sentry.io/4508064745586688";
const integrations = Sentry.getDefaultIntegrations({}).filter(
  (defaultIntegration) => !["BrowserApiErrors", "Breadcrumbs", "GlobalHandlers"].includes(defaultIntegration.name)
);
const LOCAL_EXCEPTION_GROUP_STYLING = "color: white; background-color: red; padding: 2px";
const LOCAL_MESSAGE_GROUP_STYLING = "color: white; background-color: chocolate; padding: 2px";
class ErrorTrackingClient {
  environment;
  session;
  browserClient;
  scope;
  tags;
  constructor({
    environment = getEnvironment(),
    session,
    release,
    dsn = SHARED_REACT_MFE_SENTRY_DSN,
    tags,
    ignoreErrors,
    normalizeDepth = 3,
    transport
  } = {}) {
    this.environment = environment;
    this.session = session ?? getSession();
    this.tags = tags;
    if (!transport) {
      transport = (transportOptions) => Sentry.makeFetchTransport(transportOptions);
    }
    this.browserClient = new Sentry.BrowserClient({
      dsn,
      integrations,
      ignoreErrors,
      enabled: environment !== Environment.Test,
      stackParser: Sentry.defaultStackParser,
      environment: this.environment,
      release,
      normalizeDepth,
      transport,
      attachStacktrace: true,
      // Allow captureMessage to have stack traces so that ownership rules may target them
      beforeSend(event, hint) {
        if (environment === Environment.Test) {
          return null;
        }
        if (environment !== Environment.Production && environment !== Environment.Staging) {
          const shouldSendToSentry = new URL(window.location.href).searchParams.get("sendToSentry");
          if (event.message) {
            console.groupCollapsed(
              `%cSentry Message (${event.level}): ${event.message}`,
              LOCAL_MESSAGE_GROUP_STYLING
            );
            console.info("Event:", event);
            console.info("Hint:", hint);
            if (!shouldSendToSentry) {
              console.warn("Errors in development environment aren't sent to Sentry unless the `sendToSentry` query param is set.");
            }
            console.groupEnd();
          } else {
            const error = event?.exception?.values?.[0];
            console.groupCollapsed(`%cSentry Exception: ${error?.type} \u2014 ${error?.value}`, LOCAL_EXCEPTION_GROUP_STYLING);
            console.info("Error:", error);
            console.info("Event:", event);
            console.info("Hint:", hint);
            if (!shouldSendToSentry) {
              console.warn("Errors in development environment aren't sent to Sentry unless the `sendToSentry` query param is set.");
            }
            console.groupEnd();
          }
          if (!shouldSendToSentry) {
            return null;
          }
        }
        if (window.SqOneTrust && !hasConsent(OneTrustConsent.PERFORMANCE)) {
          return null;
        }
        return event;
      }
    });
    this.scope = new Sentry.Scope();
    this.scope.setClient(this.browserClient);
    if (this.session) {
      this.setUser({
        id: this.session.merchantToken,
        merchantToken: this.session.merchantToken,
        employmentToken: this.session.employmentToken
      });
      if (this.session.merchantToken) {
        this.setTags({
          merchantToken: this.session.merchantToken
        });
      }
    }
    if (tags) {
      this.scope.setTags(tags);
    }
    this.browserClient.init();
  }
  withScope(callback) {
    const scope = new Sentry.Scope();
    scope.setClient(this.browserClient);
    return callback(scope);
  }
  setUser(session) {
    this.scope.setUser(session);
  }
  setTags(tags) {
    this.scope.setTags(tags);
  }
  setTag(tagKey, tagValue) {
    if (!this.tags) {
      this.tags = {};
    }
    this.tags[tagKey] = tagValue;
    this.scope.setTag(tagKey, tagValue);
  }
  setFingerprint(fingerprint) {
    this.scope.setFingerprint(fingerprint);
  }
  breadCrumbs({ level = "info", type = "default" /* DEFAULT */, ...breadcrumb }) {
    this.scope.addBreadcrumb({ level, type, ...breadcrumb });
  }
  captureException = (error, hint) => {
    if (this.environment === Environment.Test || !this.scope) {
      return "test";
    }
    return this.scope.captureException(error, hint);
  };
  captureMessage = (...options) => {
    if (this.environment === Environment.Test || !this.scope) {
      return "test";
    }
    const [_, __, hint] = options;
    if (hint?.data) {
      return this.withScope((scope) => {
        scope.setExtras(hint.data);
        return scope.captureMessage(...options);
      });
    }
    return this.scope.captureMessage(...options);
  };
}

export { ErrorTrackingClient as E };
